import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/dateFormatter";

const JobCoachAssignmentList = ({ onEdit }) => {
  const [assignments, setAssignments] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const searchTimeoutRef = useRef(null);

  const { user, tenant_id, loadingUser } = useContext(AuthContext);

  const fetchAssignments = useCallback(
    async (searchTerm = "") => {
      if (!tenant_id || !user) return;

      setLoading(true);
      try {
        const res = await secureAxios.get("/api/job-coach-assignments", {
          params: {
            search: searchTerm,
            tenant_id,
            user_id: user?.user_id,
            user_role: user?.role,
          },
        });
        setAssignments(res.data);
      } catch (err) {
        console.error("Error fetching assignments:", err.message);
        if (err.response?.status !== 401) {
          alert("Unable to load assignments. Please try again.");
        }
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [tenant_id, user]
  );

  // Initial load
  useEffect(() => {
    if (!loadingUser && tenant_id && user) {
      fetchAssignments();
    }
  }, [loadingUser, tenant_id, user, fetchAssignments]);

  // Handle search input change with debouncing
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearch(value);

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout
      searchTimeoutRef.current = setTimeout(() => {
        fetchAssignments(value);
      }, 500); // Wait 500ms after user stops typing
    },
    [fetchAssignments]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleEdit = (assignment) => {
    onEdit({
      ...assignment,
      tenant_id: assignment.tenant_id || tenant_id,
      start_date: assignment.start_date,
      end_date: assignment.end_date,
      consumer_id: assignment.consumer_id,
      user_id: assignment.user_id,
    });
  };

  const handleClearSearch = useCallback(() => {
    setSearch("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    fetchAssignments("");
  }, [fetchAssignments]);

  // Only show loading spinner on initial load, not during search
  if (initialLoad && loading) {
    return <div className="text-center p-3">Loading assignments...</div>;
  }

  return (
    <div>
      <div className="mb-3">
        <label className="form-label" htmlFor="assignment-search-input">
          Search Assignments:
        </label>
        <div className="input-group">
          <input
            id="assignment-search-input"
            type="text"
            placeholder="Search by Consumer or Job Coach"
            value={search}
            onChange={handleSearchChange}
            className="form-control"
            aria-label="Search assignments"
            autoComplete="off"
          />
          {search && (
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={handleClearSearch}
              title="Clear search"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
        {loading && !initialLoad && (
          <small className="text-muted">Searching...</small>
        )}
      </div>

      {/* Desktop/tablet view */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              <th scope="col">Consumer</th>
              <th scope="col">Job Coach</th>
              <th scope="col">Start Date</th>
              <th scope="col">End Date</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  {search
                    ? `No assignments found for "${search}"`
                    : "No assignments found"}
                </td>
              </tr>
            ) : (
              assignments.map((a) => (
                <tr key={a.assignment_id}>
                  <td>{a.consumer_name}</td>
                  <td>{a.user_name}</td>
                  <td>{formatDate(a.start_date)}</td>
                  <td>{a.end_date ? formatDate(a.end_date) : "-"}</td>
                  <td>
                    <span
                      className={`badge ${
                        a.end_date ? "bg-secondary" : "bg-success"
                      }`}
                    >
                      {a.end_date ? "Ended" : "Active"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleEdit(a)}
                      aria-label={`Edit assignment for ${a.consumer_name}`}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile-friendly list group view */}
      <div className="d-block d-md-none">
        {assignments.length === 0 ? (
          <div className="alert alert-info">
            {search
              ? `No assignments found for "${search}"`
              : "No assignments found"}
          </div>
        ) : (
          <ul className="list-group">
            {assignments.map((a) => (
              <li key={a.assignment_id} className="list-group-item">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{a.consumer_name}</h6>
                    <p className="mb-1">
                      <strong>Coach:</strong> {a.user_name}
                    </p>
                    <p className="mb-1">
                      <strong>Period:</strong> {formatDate(a.start_date)} -{" "}
                      {a.end_date ? formatDate(a.end_date) : "Present"}
                    </p>
                  </div>
                  <div>
                    <span
                      className={`badge ${
                        a.end_date ? "bg-secondary" : "bg-success"
                      } mb-2`}
                    >
                      {a.end_date ? "Ended" : "Active"}
                    </span>
                    <br />
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleEdit(a)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default JobCoachAssignmentList;